import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FaHome, 
  FaBoxOpen, 
  FaClipboardList, 
  FaTags,
  FaUser,
  FaCog,
  FaSignOutAlt,
  FaUsers,
  FaExchangeAlt,
  FaFileAlt
} from 'react-icons/fa';
import Logo from './Logo';
import './Navbar.css';

const Navbar = ({ user }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  // const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isMobile = window.innerWidth <= 768;

  // Navigation items with badges and descriptions
  const navItems = [
    { 
      icon: <FaHome />, 
      label: 'Dashboard', 
      path: '/admin/dashboard',
      description: 'Overview and analytics'
    },
    { 
      icon: <FaBoxOpen />, 
      label: 'Products', 
      path: '/admin/products',
      description: 'Manage your products'
    },
    { 
      icon: <FaClipboardList />, 
      label: 'Stock', 
      path: '/admin/inventory/stock',
      description: 'Inventory management'
    },
    { 
      icon: <FaTags />, 
      label: 'Categories', 
      path: '/admin/categories',
      description: 'Product categories'
    },
    { 
      icon: <FaExchangeAlt />, 
      label: 'Transactions', 
      path: '/admin/transactions',
      description: 'Order history'
    },
    { 
      icon: <FaUsers />, 
      label: 'Users', 
      path: '/admin/users',
      description: 'User management'
    },
    {
      icon: <FaFileAlt />,
      label: 'Stock Report',
      path: '/admin/reports/stock',
      description: 'Stock inventory report'
    }
  ];

  // Mobile navigation items (limited set for bottom nav)
  const mobileNavItems = [
    ...navItems.slice(0, 3),
    {
      icon: <FaSignOutAlt />,
      label: 'Logout',
      path: '#',
      description: 'Sign out',
      onClick: handleLogout
    }
  ];

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      if (currentScrollTop > lastScrollTop && currentScrollTop > 100) {
        setIsNavbarVisible(false);
      } else {
        setIsNavbarVisible(true);
      }
      setLastScrollTop(currentScrollTop);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  // Close menus when route changes
  useEffect(() => {
    setIsUserMenuOpen(false);
    // setIsMobileMenuVisible(false);
  }, [location]);

  // Handle user menu toggle
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  // Handle logout
  function handleLogout() {
    try {
      logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Check if a nav item is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className={`navbar ${!isNavbarVisible ? 'navbar-hidden' : ''}`}>
        <div className="navbar-container">
          {/* Brand */}
          <Link to="/admin/dashboard" className="navbar-brand">
            <Logo className="brand-logo" />
          </Link>

          {/* Navigation Links - Desktop */}
          {!isMobile && (
            <nav className="nav-links">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  title={item.description}
                >
                  <span className="nav-link-icon">
                    {item.icon}
                  </span>
                  <span className="nav-link-text">{item.label}</span>
                </Link>
              ))}
              <button onClick={handleLogout} className="nav-link logout-button">
                <span className="nav-link-icon">
                  <FaSignOutAlt />
                </span>
                <span className="nav-link-text">Logout</span>
              </button>
            </nav>
          )}

          {/* User Menu */}
          <div className="user-menu">
            <button className="user-button" onClick={toggleUserMenu}>
              <span className="user-avatar">
                <FaUser />
              </span>
              {!isMobile && (
                <span className="user-info">
                  <span className="user-name">{user?.email?.split('@')[0] || 'Admin'}</span>
                  <span className="user-role">Administrator</span>
                </span>
              )}
            </button>

            {isUserMenuOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <span className="user-greeting">Welcome back!</span>
                  <span className="user-email">{user?.email}</span>
                </div>
                <div className="dropdown-body">
                  <Link to="/profile" className="dropdown-item">
                    <FaUser />
                    <span>Profile</span>
                  </Link>
                  <Link to="/settings" className="dropdown-item">
                    <FaCog />
                    <span>Settings</span>
                  </Link>
                  <button onClick={handleLogout} className="dropdown-item">
                    <FaSignOutAlt />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {isMobile && (
        <nav className="mobile-nav">
          {mobileNavItems.map((item) => (
            item.onClick ? (
              <button
                key={item.path}
                onClick={item.onClick}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                title={item.description}
              >
                <span className="nav-link-icon">
                  {item.icon}
                </span>
                <span className="nav-link-text">{item.label}</span>
              </button>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                title={item.description}
              >
                <span className="nav-link-icon">
                  {item.icon}
                </span>
                <span className="nav-link-text">{item.label}</span>
              </Link>
            )
          ))}
        </nav>
      )}
    </>
  );
};

export default Navbar; 